import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdvancedSearch from './AdvancedSearch';
import recordService from '../services/RecordService';
import { useFilterContext } from '../context/FilterContext';
import { FaExternalLinkAlt, FaUser, FaSort, FaSitemap, FaExclamationTriangle } from 'react-icons/fa';
import './../styles/RecordsTable.css';
import './../styles/LoadingSpinner.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faCircleMinus, faTriangleExclamation, faUndo, faChevronUp, faEye, faStickyNote } from '@fortawesome/free-solid-svg-icons';
import useAbortableFetch from '../hooks/useAbortableFetch';  

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}


const AnalystTable = () => {
  const { filters, setFilters, setPagination } = useFilterContext();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize]       = useState(200);
  const [totalPages, setTotalPages]   = useState(1);
  const [sortColumn, setSortColumn]   = useState('recordCount');
  const [sortAsc, setSortAsc]         = useState(false);

  const [rows, setRows] = useState([]);
  const [groupCount, setGroupCount] = useState(0);
  const [totalAnalysts, setTotalAnalysts] = useState(0);
  const [localAnalystFilter, setLocalAnalystFilter] = useState('');

  useEffect(() => {
    setFilters(prev =>
      prev.analystUsername ? { ...prev, analystUsername: '' } : prev
    );
  }, [setFilters]);

  const debouncedFilters = useDebounce(filters, 300);

  const { data: tableData, error: fetchError, isLoading: fetchLoading } = useAbortableFetch(
    (signal) => {
      
      try{
      const { analystUsername: _analystUsername, ...apiFilters } = debouncedFilters;
      const payload = {
        ...apiFilters,
        pageIndex: currentPage,
        pageSize,
      };
      //need to return the new end point when ready

        return recordService.getAnalystsTable(payload, signal);
      
      } catch (err) {
        fetchError('No records found.');
        return Promise.resolve(null);
      }
    },
    [debouncedFilters, currentPage, pageSize]
  );
 
  useEffect(() => {
    if (!tableData) return;

    const {
      data = [],
      totalCount = 0,
      currentPage: respPage = currentPage,
      pageSize:    respSize = pageSize,
      totalPages:  respPages = totalPages,
      totalAnalysts = 0,
    } = tableData;

    let sortedData = [...data];
    sortedData.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      // Handle string comparison
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortAsc ? -1 : 1;
      if (aVal > bVal) return sortAsc ? 1 : -1;
      return 0;
    });

    setRows(sortedData);
    setGroupCount(totalCount);
    setTotalAnalysts(totalAnalysts);
    setCurrentPage(respPage);
    setPageSize(respSize);
    setTotalPages(respPages);
  }, [tableData, sortColumn, sortAsc]);


  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedFilters]);

  const normalizedAnalystFilter = localAnalystFilter.trim().toLowerCase();
  const filteredRows = normalizedAnalystFilter
    ? rows.filter(item =>
        (item.reviewedBy || item.analystUsername || '')
          .toLowerCase()
          .includes(normalizedAnalystFilter)
      )
    : rows;

  const handlePageChange = newPage => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    window.scrollTo(0, 0);
  };

  const handlePageSizeChange = e => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
    window.scrollTo(0, 0);
  };

  const handleSort = columnKey => {
    if (sortColumn === columnKey) {
      setSortAsc(asc => !asc);
    } else {
      setSortColumn(columnKey);
      setSortAsc(true);
    }
    setCurrentPage(1);
  };

  const handleTotalClick = () => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    navigate('/images');
  };

  const handleRecordCountClick = analystUsername => {
    setFilters(prev => ({ ...prev, analystUsername }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    navigate('/images');
  };

  const renderSortArrow = key => {
    if (sortColumn !== key) return <FaSort className="reports-font-style-grey" />;
    return sortAsc ? ' ▲' : ' ▼';
  };

  const startRecord = groupCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRecord   = Math.min(currentPage * pageSize, groupCount);

  return (
    <div>
      <AdvancedSearch onAnalystSearchChange={setLocalAnalystFilter} />

      <div className="record-custom-container analyst-table-custom-container">
        <div className="action-bar">
          <div className="pagination-controls">
            <button onClick={() => handlePageChange(1)} disabled={currentPage <= 1}>
              First
            </button>
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1}>
              Previous
            </button>
            <input
              type="number"
              value={currentPage}
              onChange={e => handlePageChange(Number(e.target.value))}
              min={1}
              max={totalPages}
            />
            <b>/</b> <b>{totalPages}</b>
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages}>
              Next
            </button>
            <button onClick={() => handlePageChange(totalPages)} disabled={currentPage >= totalPages}>
              Last
            </button>
            <select value={pageSize} onChange={handlePageSizeChange}>
              {[10, 20, 50, 100, 200].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="record-table-section">
          <div className="record-table-container">
            <div className="table-wrapper">
              {fetchLoading && (
                <div className="loading-spinner-overlay">
                  <div className="loading-spinner" />
                </div>
              )}

              {!fetchLoading && (fetchError || filteredRows.length === 0) ? (
                <div className="table-message-container">
                  <FontAwesomeIcon
                                    icon={faTriangleExclamation}
                                    size="3x"
                                    className="message-icon"
                                  />
                  <p className="table-message-text">
                    {fetchError || 'No analysts match the current search.'}
                  </p>
                </div>
              ) : (
                <table className="record-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('reviewedBy')}>
                        Analyst Username {renderSortArrow('analystUsername')}
                      </th>
                      <th className="count-clickable-header" onClick={() => handleSort('recordCount')}>
                        Record Count {renderSortArrow('recordCount')}
                        {groupCount > 0 && (
                          <span
                            className="count-circle-header clickable-count"
                            onClick={e => { e.stopPropagation(); handleTotalClick(); }}
                          >
                            {groupCount}
                          </span>
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((item, idx) => (
                      <tr key={idx}>
                        <td>
                          <FaUser className="reports-font-style-grey" /> {item.reviewedBy}
                        </td>
                        <td
                          className="record-count clickable-count"
                          onClick={() => handleRecordCountClick(item.reviewedBy)}
                        >
                          <button className="btn-view">
                            <span className="count-circle">{item.recordCount}</span> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        <div className={`table-footer ${groupCount > 0 ? 'has-entries' : 'is-empty'}`}>
          {groupCount > 0 && (
            <p className="entries-info table-footer-pill">
              {normalizedAnalystFilter
                ? `Showing ${filteredRows.length} of ${rows.length} analysts on this page`
                : `Showing ${startRecord} to ${endRecord} of ${totalAnalysts} analysts`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalystTable;
